import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { HealthApiModels } from 'models/api/health-api.models';
import { HealthRiskModels } from 'models/health-risk.models';
import { HealthModels } from 'models/health.models';
import { PolicyModels } from 'models/policy.models';
import { QuotationModels } from 'models/quotation.models';
import { SignatureModels } from 'models/signature.models';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AgencyModels } from '../../../../models/agency.models';
import { CommonModels } from '../../../../models/common.models';
import { Question } from '../components/insurances/health-quotation-formB2C/factory-age-validators';
import {
  projectApiQuotationToModel,
  projectHealthPolicyPricingMetadatasResponse,
  projectHealthQuotationRequest,
  projectSubscriptionFormToApi,
} from '../projectors/health-quotation-projector';
import { HealthQuotationApiService } from './api/health/health-quotation-api.service';
import { QuotationApiService } from './api/quotation-api.service';
import { BrokerService } from './broker.service';
import { HealthRepositoryService } from './repositories/health-repository.service';

@Injectable({
  providedIn: 'root',
})
export class HealthQuotationService {
  private _subscriptionCache!: {
    subscription: HealthModels.SubscriptionForm;
    risk: HealthRiskModels.Risk;
  };
  agencyDetails!: AgencyModels.AgencyDetails;
  saveSubscriptionCache(
    sub: HealthModels.SubscriptionForm,
    risk: HealthRiskModels.Risk,
  ) {
    this._subscriptionCache = { subscription: sub, risk };
  }

  getSubscriptionCache() {
    return this._subscriptionCache;
  }

  constructor(
    private healthQuotationApi: HealthQuotationApiService,
    private quotationApi: QuotationApiService,
    private healthRepo: HealthRepositoryService,
    private brokerService: BrokerService,
  ) {
    this.brokerService
      .getAgencyDetails()
      .subscribe((data) => (this.agencyDetails = data));
  }

  getPolicyCreationStatus(
    publicId: string,
  ): Observable<QuotationModels.QuotationStatusData> {
    return this.quotationApi.getQuotationStatus(publicId);
  }

  allowEletronic() {
    let risk = this.getSubscriptionCache().risk;
    let dateNaissance = risk.assurePrincipal.personData.dateNaissance;
    let dateNaissanceConjoint = _.get(
      risk,
      'family.conjoint.personData.dateNaissance',
    );
    let ageAssure = moment().diff(moment(dateNaissance, 'dd/MM/yyyy'), 'years');
    let allowElectronic = ageAssure < 80;
    if (dateNaissanceConjoint) {
      let ageConjoint = moment().diff(
        moment(dateNaissanceConjoint, 'dd/MM/yyyy'),
        'years',
      );
      allowElectronic = allowElectronic && ageConjoint < 80;
    }
    return allowElectronic;
  }

  getFormulas(
    risk: HealthModels.QuotationForm,
    formulaChoice: QuotationModels.FormulaChoice,
    brokerFeesChoice: QuotationModels.BrokerFeesChoice,
    controlListeParamInit: Question[],
    staggering: QuotationModels.QuittancementEnum,
    packageId: PolicyModels.PackageEnum,
    quotationParamOrigin: string | undefined,
    quotationCodeOrigin: string | undefined,
    quotationBaseOrigin: string | undefined,
    quotationAcceptCompMT: string | undefined,
    quotationNavigator: QuotationModels.Navigator | undefined,
    quotationCodeParrain: string | undefined,
  ) {
    // debugger; // utile
    return this.healthQuotationApi
      .getPricingMetadatas(
        this.getApiQuotationRequest(
          risk,
          brokerFeesChoice,
          controlListeParamInit,
          formulaChoice,
          staggering,
          packageId,
          quotationParamOrigin,
          quotationCodeOrigin,
          quotationBaseOrigin,
          quotationAcceptCompMT,
          quotationNavigator,
          quotationCodeParrain,
        ),
      )
      .pipe(
        map((res) => {
          let response = projectHealthPolicyPricingMetadatasResponse(res);
          Object.keys(response.formulas).forEach((formulaKey) => {
            if (response.formulas.hasOwnProperty(formulaKey)) {
              response.formulas[+formulaKey].universBesoinNotes =
                this.healthRepo
                  .getFormulaInformations(+formulaKey, packageId)
                  .withDPTAM.map((i) => {
                    return {
                      type: i.universType,
                      value: i.value,
                      metricType: i.valueMetricType,
                    };
                  });
            }
          });

          response.listeReponseAfi = res['listeReponseAfi'];
          return response;
        }),
      );
  }

  getSignatureInfo(quotationId: string) {
    return this.healthQuotationApi.getSignatureInfo(quotationId);
  }

  signProposition(publicId: string, signInfo: SignatureModels.SignatureInfo) {
    return this.healthQuotationApi.signProposition(publicId, {
      code: signInfo.code,
      signatureId: signInfo.signatureId,
      transactionId: signInfo.transactionId,
    });
  }

  getPricing(
    risk: HealthModels.QuotationForm,
    formulaChoice: QuotationModels.FormulaChoice,
    brokerFeesChoice: QuotationModels.BrokerFeesChoice,
    controlListeParamInit: Question[],
    staggering: QuotationModels.QuittancementEnum,
    formulas: QuotationModels.FormulaEnum[],
    packageId: PolicyModels.PackageEnum,
    quotationParamOrigin: string | undefined,
    quotationCodeOrigin: string | undefined,
    quotationBaseOrigin: string | undefined,
    quotationAcceptCompMT: string | undefined,
    quotationNavigator: QuotationModels.Navigator | undefined,
    quotationCodeParrain: string | undefined,
  ) {
    return this.healthQuotationApi.getPricing({
      formulas: formulas,
      quotationRequest: this.getApiQuotationRequest(
        risk,
        brokerFeesChoice,
        controlListeParamInit,
        formulaChoice,
        staggering,
        packageId,
        quotationParamOrigin,
        quotationCodeOrigin,
        quotationBaseOrigin,
        quotationAcceptCompMT,
        quotationNavigator,
        quotationCodeParrain,
      ),
    });
  }

  private getApiQuotationRequest(
    risk: HealthModels.QuotationForm,
    brokerFeesChoice: QuotationModels.BrokerFeesChoice,
    controlListeParamInit: Question[],
    formulaChoice: QuotationModels.FormulaChoice,
    staggering: QuotationModels.QuittancementEnum,
    packageId: PolicyModels.PackageEnum,
    quotationParamOrigin: string | undefined,
    quotationCodeOrigin: string | undefined,
    quotationBaseOrigin: string | undefined,
    quotationAcceptCompMT: string | undefined,
    quotationNavigator: QuotationModels.Navigator | undefined,
    quotationCodeParrain: string | undefined,
  ): HealthApiModels.QuotationRequest {
    // debugger; // utile
    return projectHealthQuotationRequest({
      risk: this.projectQuotationFormToModel(risk),
      brokerFees: brokerFeesChoice ? brokerFeesChoice.management : undefined,
      controlListeParam: controlListeParamInit,
      quotationParamOrigin: quotationParamOrigin,
      quotationCodeOrigin: quotationCodeOrigin,
      quotationCodeParrain: quotationCodeParrain,
      quotationBaseOrigin: quotationBaseOrigin,
      quotationAcceptCompMT: quotationAcceptCompMT,
      quotationNavigator: quotationNavigator,
      brokerLoading: brokerFeesChoice ? brokerFeesChoice.loading : undefined,
      formula: formulaChoice ? formulaChoice.formula : undefined,
      optionalWarranties: formulaChoice ? formulaChoice.optionalWarranties : [],
      commissionType: brokerFeesChoice ? brokerFeesChoice.type : undefined,
      staggering: staggering,
      packageId: packageId,
    });
  }

  private setDateFormatReverse() {
    var date360 = new Date();
    date360.setDate(date360.getDate() + 360);

    var day = date360.getDate().toString();
    if (day.length < 2) {
      day = '0' + day;
    }
    var month = (date360.getMonth() + 1).toString();
    if (month.length < 2) {
      month = '0' + month;
    }
    var year = date360.getFullYear();
    var valiny = month + '/' + day + '/' + year;
    return valiny;
  }

  private projectQuotationFormToModel(
    quotationForm: HealthModels.QuotationForm,
  ): HealthRiskModels.Risk {
    var modelRisk: HealthRiskModels.Risk | any = {
      assure: quotationForm.assureInfos
        ? quotationForm.assureInfos
        : {
            nom: 'RAKOTO',
            prenom: 'Mandimby',
            email: 'email@yahoo.email',
            civilite: CommonModels.CivilityEnum.Femme,
            address: {
              streetNumber: 1,
              //cityName: quotationForm.assurePrincipal.groupVille.ville,
              cityName: "Villenave-D'Ornon",
              streetName: 'les Bosquets de Chambéry',
              //codeInsee: quotationForm.assurePrincipal.groupVille.inseeCode,
              codeInsee: '012541',
              //cityZipCode: quotationForm.assurePrincipal.groupVille.codePostal,
              cityZipCode: '33140',
              streetType: 'Lotissement2',
            },
            phone: '0602124512',
            mobile: '0326124556',
            //birthPlace: quotationForm.assurePrincipal.groupVille.ville,

            birthPlace: 'Paris',
            birthCountry: 'France',
            doAgreeToReceiveInfosByEmail: false,
            isPoliticallyExposed: false,
            isEntournagePoliticallyExposed: false,
            doCertify: true,
          },
      assurePrincipal: {
        personData: {
          isDisabled: false,
          civility: quotationForm.assureInfos?.civilite
            ? quotationForm.assureInfos.civilite
            : CommonModels.CivilityEnum.Homme,
          dateNaissance: quotationForm?.assurePrincipal.dateNaissance
            ? quotationForm.assurePrincipal.dateNaissance
            : '10/10/1995',
        },
        securiteSocialData: {
          //regimeSocial: quotationForm.assurePrincipal.regimeSocial
          regimeSocial: quotationForm.assurePrincipal.regimeSocial
            ? quotationForm.assurePrincipal.regimeSocial
            : 1,
        },
      },
      vadInfo: quotationForm.vadInfo
        ? quotationForm.vadInfo
        : {
            isVad: true,
            saleType: 1,
            isCallFromProspect: true,
            isRappel: false,
            isBloctel: false,
            toggleBloctel: true,
            toggleVad: null,
            signatureReadyDate: null,
            tarifier: 'oui_init',
          },
      //universBesoinInfo: quotationForm.universBesoinInfo,
      universBesoinInfo: quotationForm.universBesoinInfo
        ? quotationForm.universBesoinInfo!
        : {
            hasAlreadyHealthContract: false,
            healthContractName: null,
            monthlyBudget: 9999,
            formula: null,
            questions: [
              {
                level: 1,
                type: 2,
              },
              {
                level: 1,
                type: 1,
              },
              {
                level: 1,
                type: 5,
              },
              {
                level: 1,
                type: 4,
              },
              {
                level: 1,
                type: 3,
              },
            ],
          },
      effectDate: quotationForm.assurePrincipal.dateEffet
        ? quotationForm.assurePrincipal.dateEffet
        : this.setDateFormatReverse(),
      //effectDate: "12/12/2022",

      family: {
        //conjoint: this.mapAssure(quotationForm.family.conjoint),
        conjoint: undefined,
        //enfants: quotationForm.family.enfants.map(this.mapAssure)
        enfants: undefined,
      },
    };

    return modelRisk;
  }

  createQuotation(data: HealthModels.HealthQuotationStoredData) {
    // debugger; // utile
    return this.healthQuotationApi.createQuotation({
      hasToSendQuotationToClient: true,
      quotationRequest: projectHealthQuotationRequest({
        risk: this.projectQuotationFormToModel(data.quotationForm),
        brokerFees: data.brokerFees.management,
        controlListeParam: data.controlListeParam,
        brokerLoading: data.brokerFees.loading,
        formula: data.formulaChoice.formula,
        quotationParamOrigin: data.quotationParamOrigin,
        quotationCodeOrigin: data.quotationCodeOrigin,
        quotationCodeParrain: data.quotationCodeParrain,
        quotationBaseOrigin: data.quotationBaseOrigin,
        quotationAcceptCompMT: data.quotationAcceptCompMT,
        quotationNavigator: data.quotationNavigator,
        optionalWarranties: data.formulaChoice.optionalWarranties,
        commissionType: data.brokerFees.type,
        staggering: data.currentStaggering,
        packageId: data.package,
        tarifPrix: data.tarifPrix,
        codeFormule: data.codeFormule,
        TypeProduit: data.TypeProduit,
      }),
    });
  }
  // createQuotationControlList(data: HealthModels.HealthQuotationStoredData) {
  //     return this.healthQuotationApi.createQuotation({
  //         hasToSendQuotationToClient: true,
  //         quotationRequest: projectHealthQuotationRequest({
  //             risk: this.projectQuotationFormToModel(data.quotationForm),
  //             brokerFees: 1,
  //             controlListeParam: data.controlListeParam,
  //             brokerLoading: 2,
  //             //formula: QuotationModels.FormulaEnum.Sante1,
  //             formula: data.formulaChoice.formula,
  //             optionalWarranties: data.formulaChoice.optionalWarranties,
  //             commissionType: data.brokerFees.type,
  //             staggering: data.currentStaggering,
  //             packageId: data.package
  //         })

  //     });
  // }

  getQuotation(publicId: string): Observable<HealthModels.QuotationSummary> {
    return this.healthQuotationApi
      .getQuotation(publicId)
      .pipe(map(projectApiQuotationToModel));
  }

  createProposition(
    publicId: string,
    subscriptionForm: HealthModels.SubscriptionForm,
  ): Observable<any> {
    return this.healthQuotationApi.createProposition(
      publicId,
      projectSubscriptionFormToApi(subscriptionForm),
    );
  }

  getFormulaInformations(
    formula: QuotationModels.FormulaEnum,
    packageId: PolicyModels.PackageEnum,
  ) {
    return of(this.healthRepo.getFormulaInformations(formula, packageId));
  }

  sendSignedPropositionFile(publicId: string, file: File[]) {
    return this.healthQuotationApi.postSignedPropositionFile(publicId, file);
  }
}
